// Forte Checkout v2 (checkout.forte.net/v2/js) is loaded by FortePayment.vue. The moment its
// pay button is activated it appends a modal container straight onto document.body:
//
//   <div class="fco2-iframe-container"><iframe id="imodalN" class="iframe-wrapper fco2"></iframe></div>
//
// plus a hidden <form id="fmodalN" target="imodalN"> beside the button, which it submits
// 500 ms later to give the iframe its page. Forte's own stylesheet makes that iframe
// position:absolute, 100% x 100%, z-index 99999999, pointer-events:all, and Forte only
// removes it from its payment callback (abort/success/failure).
//
// If the dialog holding the button closes before that deferred submit - Enter on the
// focused button both activated Forte AND saved the deposit - the iframe never gets a page.
// It stays blank and invisible, swallows every click on the app, and steals keyboard focus,
// so Tab walks out to the browser toolbar. Only a reload cleared it (Washington OD,
// TKT-000547 / 000577 / 000708). These helpers find and remove those orphans.

export const FORTE_CONTAINER_SELECTOR = '.fco2-iframe-container';

// Forte submits its hidden form 500 ms after the click. Sweep a little after that so a
// checkout the user genuinely opened is left alone.
export const FORTE_ORPHAN_SWEEP_DELAY_MS = 1500;

// A targeted form POST navigates the iframe WITHOUT touching its src attribute, so src alone
// cannot tell a live checkout from a blank one. Once Forte's page has loaded the frame is
// cross-origin and reading its location throws - that is the reliable "live" signal.
export const isBlankForteContainer = (container) => {
  const frame = container && container.querySelector('iframe');
  if (!frame) return true;
  if (frame.getAttribute('src')) return false;
  try {
    // Readable location => same-origin frame => still the empty page Forte created.
    const win = frame.contentWindow;
    if (win && win.location) { const { href } = win.location; return typeof href !== 'string' || href.indexOf('forte') === -1; }
    return true;
  } catch (e) {
    return false; // cross-origin content is loaded: this is Forte's live checkout page
  }
};

// Forte pairs iframe "imodalN" with form "fmodalN".
const forteFormFor = (doc, frame) => {
  const id = frame && frame.id;
  if (!id || id.charAt(0) !== 'i') return null;
  return doc.getElementById(`f${id.slice(1)}`);
};

// Removes every Forte modal container whose iframe was never given a page, together with the
// hidden form Forte parked for that container. Returns how many containers were removed.
export const removeOrphanForteContainers = (doc = document) => {
  let removed = 0;
  doc.querySelectorAll(FORTE_CONTAINER_SELECTOR).forEach((container) => {
    if (!isBlankForteContainer(container)) return;
    const form = forteFormFor(doc, container.querySelector('iframe'));
    if (form) form.remove();
    container.remove();
    removed += 1;
  });
  return removed;
};

// Schedules a sweep once Forte's deferred submit has had its chance to run. Returns the timer
// id so the caller can cancel it if a new checkout may start before it fires.
export const sweepOrphanForteContainersLater = (doc = document, delay = FORTE_ORPHAN_SWEEP_DELAY_MS) => (
  setTimeout(() => removeOrphanForteContainers(doc), delay)
);
